import { flatMap } from 'lodash';
import { useEffect, useState } from 'react';
import type { com } from '@polarsteps/polarsteps-shared-core';
import { parse } from 'utils/error-parser';
import { withCache } from 'utils/fetch-cache';
import { get } from 'utils/http';
import { loadSharedCodebase } from 'utils/use-shared-codebase';

export const SPOT_CATEGORY_FIRST_LEVEL_SWR_KEY = 'SPOT_CATEGORY_FIRST_LEVEL_SWR_KEY';
export const SPOT_CATEGORY_SECOND_LEVEL_SWR_KEY = 'SPOT_CATEGORY_SECOND_LEVEL_SWR_KEY';

async function getCategoryTree() {
    const sharedCodebase = await loadSharedCodebase();
    const categoryTreeJson = await withCache('CATEGORY_TREE_URL_json', () =>
        get(sharedCodebase.com.polarsteps.shared.core.CATEGORY_TREE_URL, {
            credentials: 'omit',
        })
    );
    const core = sharedCodebase.polarstepsCore();
    return core.createCategoryTreeWithData(JSON.stringify(categoryTreeJson));
}

/**
 * Gets all the first level (category) ids of categories
 */
export const getAllFirstLevel = async (): Promise<SpotCategory[]> => {
    const categoryTree = await getCategoryTree();
    return getFirstLevelCategoriesFromTree(categoryTree)
        .map((category) => category.id)
        .map(expandToObjectWithId);
};

function getFirstLevelCategoriesFromTree(categoryTree: com.polarsteps.shared.service.CoreCategoryTree) {
    const mainCategories = categoryTree.mainCategories().asJsReadonlyArrayView();
    const allFirstLevel = flatMap(mainCategories, (mainCategory: com.polarsteps.shared.domain.Category) => {
        const children = mainCategory.children;
        return children ? children.asJsReadonlyArrayView() : [];
    });

    return allFirstLevel.filter((category) => category.categoryLevel() === 'ps_category');
}

/**
 * Gets all the second level (sub_category) of the tree
 */
export const getAllSecondLevel = async (): Promise<SpotCategory[]> => {
    const categoryTree = await getCategoryTree();
    const allFirstLevel = getFirstLevelCategoriesFromTree(categoryTree);
    const allSecondLevel = flatMap(allFirstLevel, (firstLevel) => {
        const children = firstLevel.children;
        return children ? children.asJsReadonlyArrayView() : [];
    });

    return allSecondLevel
        .filter((category) => category.categoryLevel() === 'ps_sub_category')
        .map((category) => category.id)
        .map(expandToObjectWithId);
};

export const useAllSecondLevel = () => {
    const [reloadCounter, setReloadCounter] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [allCategories, setAllCategories] = useState<SpotCategory[] | null>(null);

    useEffect(() => {
        setIsLoading(true);
        setError(null);
        getAllSecondLevel()
            .then((categories) => {
                setAllCategories(categories);
            })
            .catch((e) => {
                setError(parse(e));
            })
            .finally(() => {
                setIsLoading(false);
            });
    }, [reloadCounter]);

    const reload = () => {
        setReloadCounter((old) => old + 1);
    };

    return {
        isLoading,
        error,
        allCategories: (allCategories ?? []).sort((a, b) => a.name.localeCompare(b.name)),
        reload,
    };
};

/**
 * Gets all the second level (sub_category) ids of categories for first level parent
 */
export const getSecondLevel = async (firstLevelParent: string): Promise<SpotCategory[]> => {
    const categoryTree = await getCategoryTree();
    const allFirstLevel = getFirstLevelCategoriesFromTree(categoryTree);

    const firstLevelCategory = allFirstLevel.find(
        (category: com.polarsteps.shared.domain.Category) => category.id === firstLevelParent
    );
    const children = firstLevelCategory?.children;
    const childrenArray = children ? children.asJsReadonlyArrayView() : [];
    return childrenArray
        .filter((category: com.polarsteps.shared.domain.Category) => category.categoryLevel() === 'ps_sub_category')
        .map((category: com.polarsteps.shared.domain.Category) => category.id)
        .map(expandToObjectWithId);
};

function expandToObjectWithId(property: string): SpotCategory {
    return {
        id: property,
        name: property,
    };
}
